import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';

interface User {
    username: string;
}

interface AuthContextType {
    user: User | null;
    login: (username: string, password: string) => Promise<boolean>;
    logout: () => Promise<void>;
    isLoading: boolean;
    isAuthenticated: boolean;
    token: string | null;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
    const context = useContext(AuthContext);
    if (context === undefined) {
        throw new Error('useAuth must be used within an AuthProvider');
    }
    return context;
};

interface AuthProviderProps {
    children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
    const [user, setUser] = useState<User | null>(null);
    const [token, setToken] = useState<string | null>(null);
    const [isLoading, setIsLoading] = useState(true);

    const API_BASE_URL = import.meta.env.VITE_API_URL || '/api';

    // Check if user is authenticated on app load
    useEffect(() => {
        checkAuthStatus();
    }, []);

    const checkAuthStatus = async () => {
        // Get token from localStorage
        const storedToken = localStorage.getItem('logwatch_token');

        if (!storedToken) {
            setIsLoading(false);
            return;
        }

        try {
            // Verify token with server
            const response = await fetch(`${API_BASE_URL}/auth/me`, {
                headers: {
                    Authorization: `Bearer ${storedToken}`
                }
            });

            if (response.ok) {
                const data = await response.json();
                if (data.authenticated) {
                    setUser(data.user);
                    setToken(storedToken);
                } else {
                    // Server responded but says not authenticated - remove token
                    localStorage.removeItem('logwatch_token');
                }
            } else if (response.status === 401) {
                // Only remove token if server explicitly says it's unauthorized (401)
                // Don't remove on network errors or server unavailable (500, etc.)
                localStorage.removeItem('logwatch_token');
            }
            // For any other status codes (500, 502, etc.), keep the token
            // The server might just be restarting
        } catch (error) {
            // Network error (server down/restarting) - keep the token
            console.log('Auth check failed due to network error (server might be restarting):', error);

            // If we have a stored token, assume it's still valid until we can verify
            if (storedToken) {
                setToken(storedToken);
                // Set a placeholder user to maintain logged-in state
                setUser({ username: 'unknown' });
            }
        } finally {
            setIsLoading(false);
        }
    };

    const login = async (username: string, password: string): Promise<boolean> => {
        try {
            const response = await fetch(`${API_BASE_URL}/auth/login`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({ username, password })
            });

            if (response.ok) {
                const data = await response.json();
                const { token: newToken, user: userData } = data;

                // Store token in localStorage
                localStorage.setItem('logwatch_token', newToken);
                setToken(newToken);
                setUser(userData);
                return true;
            } else {
                return false;
            }
        } catch (error) {
            console.error('Login failed:', error);
            return false;
        }
    };

    const logout = async (): Promise<void> => {
        try {
            // Call logout endpoint (optional for JWT)
            if (token) {
                await fetch(`${API_BASE_URL}/auth/logout`, {
                    method: 'POST',
                    headers: {
                        Authorization: `Bearer ${token}`
                    }
                });
            }
        } catch (error) {
            console.error('Logout failed:', error);
        } finally {
            // Clear token and user data
            localStorage.removeItem('logwatch_token');
            setToken(null);
            setUser(null);
        }
    };

    const value = {
        user,
        login,
        logout,
        isLoading,
        isAuthenticated: !!user,
        token
    };

    return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
